import React, { useState } from "react";
import {
    DialogContent,
    DialogActions,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    FormControlLabel,
    Button,
    Grid,
    Switch,
    Typography,
    Stack,
    useMediaQuery,
    Fab,
    Tooltip,
    CircularProgress
} from "@mui/material";
import BlockedTimeModal from "./BlockedTimeModal";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { listProviderSchedules } from "../../graphql/queries";
import {
    allBlockedTimesAtom,
    displayBlockedTimesAtom,
    selectedBuIdAtom,
    recordIdAtom,
    openBlockedTimeDialogAtom,
    onlyCurrentBlockedTimesAtom
} from "../../atoms/blockedTime";
import AddIcon from "@mui/icons-material/Add";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { DialogTheme } from "../../styles/DialogTheme";
import { MobileBookingsTheme } from "../../styles/MobileBookingsTheme";
import MobileBlockedTimeCard from "../../components/mobile/MobileBlockedTimeCard";
import { daysArrayToString } from "../../modules/RecurringBookingsUtilityFunctions";
import moment from "moment";
import { execReadBySortkey } from "../../modules/DBService";
import { isMbxUserManager, mbxUserAtom } from "../../atoms/atoms";
import { selectedUserAtom } from "../../atoms/usersTable";
import { GOOGLE_CAL_INTEGRATION_ID } from "../../utils/Constants";
import { jsonParse } from "../../utils";

const BlockedTimeTab = ({ onClose }) => {
    const theme = useTheme();
    const [openBlockedTimeDialog, setBlockedTimeDialog] = useAtom(
        openBlockedTimeDialogAtom
    );
    const [mbxUser] = useAtom(mbxUserAtom);
    const [isLoggedInUserManager] = useAtom(isMbxUserManager);
    const [selectedUser] = useAtom(selectedUserAtom);
    const [selectedRow, setSelectedRow] = useState();
    const [showExpired, setShowExpired] = useState(false);
    const [recordId] = useAtom(recordIdAtom);
    const [displayBlockedTimeRows] = useAtom(displayBlockedTimesAtom);
    const [onlyCurrentBlockedTimes] = useAtom(onlyCurrentBlockedTimesAtom);
    const [, setAllBlockedTimes] = useAtom(allBlockedTimesAtom);
    const [, setSelectedBuId] = useAtom(selectedBuIdAtom);
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const isManagerView =
        isLoggedInUserManager &&
        mbxUser.id !== selectedUser.id &&
        selectedUser.role !== "PROVIDER";

    const disableAllFields = isManagerView;
    const { refetch, isFetching } = useQuery({
        queryKey: ["get-blocked-times"],
        queryFn: async () => {
            try {
                let { items, error } = await execReadBySortkey({
                    opname: "listProviderSchedules",
                    op: listProviderSchedules,
                    id: {
                        id: recordId
                    },
                    skey: {
                        scheduleinfo: { beginsWith: "BU" }
                    },
                    filter: { deleted: { ne: true } },
                    limit: 500
                });
                if (error) throw error;
                return items;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (blockedTimes) => {
            setAllBlockedTimes(blockedTimes);
        }
    });

    const handleBlockedTimeDialog = () => {
        setBlockedTimeDialog(true);
    };

    const handleCloseBlockedTimeDialog = () => {
        setBlockedTimeDialog(false);
    };

    const getVisibleRows = () => {
        let visibleRows = [];
        if (showExpired) {
            visibleRows = displayBlockedTimeRows || [];
        } else {
            visibleRows = onlyCurrentBlockedTimes || [];
        }
        return visibleRows.filter(
            ({ MetaData }) =>
                !MetaData ||
                jsonParse(MetaData, {}).origin !== GOOGLE_CAL_INTEGRATION_ID
        );
    };

    const lastRowIndex = displayBlockedTimeRows.length - 1; // Index of the last row

    return (
        <ThemeProvider theme={DialogTheme(theme)}>
            <BlockedTimeModal
                open={openBlockedTimeDialog}
                onClose={() => {
                    handleCloseBlockedTimeDialog();
                    refetch();
                }}
                blockedTimeData={selectedRow}
            />

            <DialogContent sx={{ minHeight: "200px" }}>
                {isDesktop ? (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell variant="head">
                                        Date range
                                    </TableCell>
                                    <TableCell variant="head">
                                        Time range
                                    </TableCell>
                                    <TableCell variant="head">
                                        Days of week
                                    </TableCell>
                                    <TableCell variant="head">Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            {isFetching && !displayBlockedTimeRows.length ? (
                                <TableBody>
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            sx={{
                                                textAlign: "center",
                                                padding: "32px !important",
                                                borderBottom: "none"
                                            }}
                                        >
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            ) : (
                                <TableBody>
                                    {getVisibleRows().map((row, index) => (
                                        <TableRow
                                            key={index}
                                            hover={!disableAllFields}
                                            onClick={
                                                !disableAllFields
                                                    ? () => {
                                                          setSelectedRow(index);
                                                          setBlockedTimeDialog(
                                                              true
                                                          );
                                                          setSelectedBuId(
                                                              row.buId
                                                          );
                                                      }
                                                    : undefined
                                            }
                                            sx={{
                                                cursor: !disableAllFields
                                                    ? "pointer"
                                                    : "default",
                                                opacity: !disableAllFields
                                                    ? 1
                                                    : 0.5,
                                                pointerEvents: !disableAllFields
                                                    ? "auto"
                                                    : "none"
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    minWidth: "225px",
                                                    maxWidth: "225px",
                                                    ...(index ===
                                                        lastRowIndex && {
                                                        borderBottom: "none"
                                                    })
                                                }}
                                            >
                                                {row.startDate === row.endDate
                                                    ? `${moment(
                                                          row.startDate
                                                      ).format("MMM D, YYYY")}`
                                                    : row.dateRange}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: "150px",
                                                    maxWidth: "150px",
                                                    ...(index ===
                                                        lastRowIndex && {
                                                        borderBottom: "none"
                                                    })
                                                }}
                                            >
                                                {row.timeRange}
                                            </TableCell>
                                            <TableCell
                                                sx={
                                                    index === lastRowIndex
                                                        ? {
                                                              borderBottom:
                                                                  "none"
                                                          }
                                                        : {}
                                                }
                                            >
                                                {daysArrayToString(
                                                    row.weekDays
                                                        .split("")
                                                        .map(Number)
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    position: "relative",
                                                    overflow: "hidden",
                                                    ...(index ===
                                                        lastRowIndex && {
                                                        borderBottom: "none"
                                                    })
                                                }}
                                            >
                                                {row.notes && (
                                                    <Tooltip
                                                        title={
                                                            row.notes.length >
                                                            95
                                                                ? row.notes
                                                                : ""
                                                        }
                                                        arrow
                                                    >
                                                        <Typography
                                                            sx={{
                                                                display:
                                                                    "block",
                                                                fontStyle:
                                                                    "italic",
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                lineHeight:
                                                                    "14px",
                                                                maxHeight:
                                                                    "63px"
                                                            }}
                                                        >
                                                            {row.notes.length >
                                                            95
                                                                ? `"${row.notes.slice(
                                                                      0,
                                                                      95
                                                                  )}..."`
                                                                : `"${row.notes}"`}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                ) : (
                    <ThemeProvider theme={MobileBookingsTheme(theme)}>
                        {isFetching && !displayBlockedTimeRows.length ? (
                            <Grid container justifyContent={"center"} p={4}>
                                <CircularProgress />
                            </Grid>
                        ) : (
                            <Stack>
                                {getVisibleRows().map((row) => (
                                    <MobileBlockedTimeCard blockedTime={row} />
                                ))}
                            </Stack>
                        )}
                        <Fab
                            onClick={() => {
                                setSelectedBuId();
                                handleBlockedTimeDialog();
                            }}
                        >
                            <AddIcon
                                sx={{
                                    color: "white",
                                    fontSize: "2rem !important"
                                }}
                            />
                        </Fab>
                    </ThemeProvider>
                )}
            </DialogContent>
            {isDesktop && (
                <DialogActions
                    sx={{ display: "flex", justifyContent: "space-between" }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showExpired}
                                onChange={(e) =>
                                    setShowExpired(e.target.checked)
                                }
                            />
                        }
                        label="Show expired blocked times"
                    />

                    <Grid sx={{ display: "flex", gap: "18px" }}>
                        <Button
                            onClick={() => {
                                onClose();
                                setSelectedBuId();
                            }}
                            variant="cancelButton"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon sx={{ color: "white" }} />}
                            disabled={disableAllFields}
                            onClick={() => {
                                setSelectedBuId();
                                handleBlockedTimeDialog();
                            }}
                        >
                            <Typography
                                variant="button"
                                sx={{ fontWeight: "600", color: "white" }}
                            >
                                {" "}
                                Add Blocked Time
                            </Typography>
                        </Button>
                    </Grid>
                </DialogActions>
            )}
        </ThemeProvider>
    );
};

export default BlockedTimeTab;
